/**
 * View-model for rendering a Gemonix hotel row + selected LDS legs as a unified "vacation package" card.
 * Hotel list price on the card: raw API `LowestPackagePrice` only (no FX / commission / flight supplement).
 */

import { legToPremiumSummary } from "../premiumPackageCardModel";
import {
  agentHotelSearchLowestPackagePrice,
  formatAgentHotelSearchApiPriceDisplay,
} from "../agentHotelSearchApiPrice";
import {
  buildHotelSearchCardHeroImageUrls,
  buildHotelSearchResultLocationFields,
  computeNightsFromSearchPhrase,
  getHotelSearchStarRating,
  resolveHotelSearchBoardLabel,
  translateBoardCode,
} from "../hotelSearchListDisplay";
import { getLdsBoardBasisLabelHebrew } from "../ldsBoardBasisHebrew";
import {
  buildNormalizedDynamicPackagePricing,
  buildDynamicPackageSearchResultBreakdownRows,
  resolveDynamicPackagePricingDealRoot,
} from "../dynamicPackage/dynamicPackageNormalizedPricing";
import { isDynamicPackagePricingNormalizedEnabled } from "../dynamicPackage/dynamicPackagePricingFeatureFlag";
import { pickCheapestDynamicPackageSearchFlightPrice } from "../dynamicPackage/dynamicPackageSearchCheapestFlight";

export function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function firstDefined(...vals) {
  for (const x of vals) {
    if (x !== undefined && x !== null && x !== "") return x;
  }
  return null;
}

export function safeArray(x) {
  if (x == null) return [];
  return Array.isArray(x) ? x : [x];
}

/**
 * Normalize flight selection to an LDS leg shape for legToPremiumSummary.
 * selectedDepart / selectedReturn are usually leg objects; tolerate flight wrappers.
 */
export function normalizeFlightSegment(flightOrLeg) {
  if (!flightOrLeg || typeof flightOrLeg !== "object") return null;
  if (
    flightOrLeg.depDate != null &&
    (flightOrLeg.depAirport != null ||
      flightOrLeg.depCity != null ||
      flightOrLeg.arrAirport != null ||
      flightOrLeg.arrCity != null)
  ) {
    return flightOrLeg;
  }
  const f0 = flightOrLeg.flight?.[0] ?? flightOrLeg.flight;
  if (f0?.leg) {
    const L = f0.leg;
    return Array.isArray(L) ? L[0] : L;
  }
  return null;
}

/** @returns {{ hotelApiPrice: number|null }} */
function extractHotelSearchApiLowestPrice(hotelsItem) {
  return { hotelApiPrice: agentHotelSearchLowestPackagePrice(hotelsItem) };
}

export function extractFlightPriceIls(linkedFlightsDealPrice) {
  return toNumber(linkedFlightsDealPrice);
}

function countPaxFromRooms(rooms) {
  if (!Array.isArray(rooms) || rooms.length === 0) return 2;
  let n = 0;
  rooms.forEach((room) => {
    n += toNumber(room.AdultsCount ?? room.adults ?? 0);
    const kids = room.KidsAges?.length ?? room.kids ?? 0;
    n += toNumber(kids);
  });
  return Math.max(1, n);
}

function resolveDynamicBoardLabel({
  t,
  boardName,
  boardCode,
  hotelBoards,
  selectedBoardFilters,
}) {
  if (boardName && String(boardName).trim()) {
    const trimmed = String(boardName).trim();
    if (/^[A-Za-z]{1,4}$/.test(trimmed) && typeof t === "function") {
      const viaT = translateBoardCode(trimmed, t);
      if (viaT && viaT !== trimmed.toUpperCase()) return viaT;
    }
    if (/[a-zA-Z]/.test(trimmed)) {
      const mapped = getLdsBoardBasisLabelHebrew(trimmed);
      if (mapped) return mapped;
    }
    return trimmed;
  }
  if (typeof t !== "function") return null;
  if (boardCode != null && String(boardCode).trim()) {
    return translateBoardCode(boardCode, t) || String(boardCode).trim();
  }
  return resolveHotelSearchBoardLabel(hotelBoards, selectedBoardFilters, t);
}

/**
 * @param {object} params
 * @param {import("i18next").TFunction} [params.t] — required for board labels to match hotel search i18n
 * @param {object|null} [params.savedDeal] — SearchContext saved LDS deal (dealFare / fullDeal)
 * @returns {object} view model for PremiumPackageCard + debug raw
 */
export function buildDynamicVacationPackageViewModel(params = {}) {
  const {
    hotelsItem = null,
    selectedDepart = null,
    selectedReturn = null,
    linkedFlightsDealPrice = 0,
    savedDeal = null,
    searchPhrase = null,
    hotelBoards = [],
    selectedBoardFilters = [],
    boardCode = null,
    boardName = null,
    t = null,
    debugTag = "buildDynamicVacationPackageViewModel",
    flightTripType = "RoundTrip",
    roundTripFlightResults = null,
    outboundOneWayFlightResults = null,
    inboundOneWayFlightResults = null,
    availableFlights = null,
    /** כשמופעל — ללא מודל מחיר מנורמל ישן לכרטיס תוצאות (המחיר מגיע מ-SSOT חיצוני). */
    skipSearchCardLegacyNormalizedPricing = false,
  } = params;

  const raw = {
    hotelsItem,
    selectedDepart,
    selectedReturn,
    linkedFlightsDealPrice,
    savedDeal,
  };

  const id =
    hotelsItem?.ID ?? hotelsItem?.Id ?? hotelsItem?.id ?? hotelsItem?.hotel_id;
  const hotelName = firstDefined(
    hotelsItem?.DisplayName,
    hotelsItem?.Name,
    hotelsItem?.HotelName,
    hotelsItem?.hotelName
  );

  const stars = getHotelSearchStarRating(hotelsItem);

  const { locationLine, locationCity, locationCountry } =
    buildHotelSearchResultLocationFields(hotelsItem, searchPhrase);

  const heroImageUrls = buildHotelSearchCardHeroImageUrls(hotelsItem);
  const heroImageUrl = heroImageUrls[0] || null;

  const nights = computeNightsFromSearchPhrase(searchPhrase);
  const passengersCount = countPaxFromRooms(searchPhrase?.rooms);

  const boardLabel = resolveDynamicBoardLabel({
    t,
    boardName,
    boardCode,
    hotelBoards,
    selectedBoardFilters,
  });

  const depLeg = normalizeFlightSegment(selectedDepart);
  const retLeg = normalizeFlightSegment(selectedReturn);
  const outbound = depLeg ? legToPremiumSummary(depLeg) : null;
  const inbound = retLeg ? legToPremiumSummary(retLeg) : null;

  if (process.env.NODE_ENV === "development") {
    if (!depLeg) {
      console.log(`[dynamic-packages] ${debugTag}: missing outbound leg for vm`, {
        hotelId: id,
      });
    }
    if (!retLeg && searchPhrase == null) {
      /* one-way: ok */
    } else if (!retLeg && searchPhrase?.checkOut) {
      console.log(`[dynamic-packages] ${debugTag}: missing return leg for vm`, {
        hotelId: id,
      });
    }
  }

  const { hotelApiPrice } = extractHotelSearchApiLowestPrice(hotelsItem);
  const flightsPriceIls = extractFlightPriceIls(linkedFlightsDealPrice);

  const reqCur = searchPhrase?.currency
    ? String(searchPhrase.currency).trim().toUpperCase()
    : null;

  let normalizedPricing;
  let pricingTrace;
  let breakdownRows;
  let finalPrice;
  let priceLineParts;
  let currency;
  let searchCheapestFlight = { rawFlightSources: {} };

  if (skipSearchCardLegacyNormalizedPricing) {
    normalizedPricing = null;
    pricingTrace = null;
    breakdownRows = [];
    finalPrice = null;
    priceLineParts = null;
    currency = "ILS";
  } else {
    const lf = savedDeal?.linkedFlights;
    const hasLinkedFlightsStructure = Boolean(
      lf &&
        (Array.isArray(lf)
          ? lf.length > 0
          : typeof lf === "object" &&
            (Boolean(lf.flight) || Object.keys(lf).length > 0))
    );

    searchCheapestFlight = pickCheapestDynamicPackageSearchFlightPrice({
      flightTripType,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
      availableFlights,
    });

    const rowDealRootForTrace =
      resolveDynamicPackagePricingDealRoot(hotelsItem) ||
      resolveDynamicPackagePricingDealRoot(hotelsItem?.fullDeal);

    const normResult = buildNormalizedDynamicPackagePricing({
      stage: "search_card",
      savedDeal,
      fullDeal: savedDeal,
      hotelsItem,
      searchListCurrency: reqCur,
      linkedFlightsDealPriceIls: flightsPriceIls,
      hasLinkedFlightsStructure,
      normalizedPricingEnabled: isDynamicPackagePricingNormalizedEnabled(),
      searchCheapestFlight,
    });
    normalizedPricing = normResult.normalized;
    pricingTrace = normResult.pricingTrace;

    if (import.meta.env.DEV && hotelsItem) {
      try {
        console.log("[DYNAMIC_RESULT_CHEAPEST_FLIGHT_TRACE]", {
          hotelName,
          resultCardId: id,
          linkedFlights: hotelsItem?.linkedFlights ?? savedDeal?.linkedFlights,
          dealFare:
            rowDealRootForTrace?.dealFare ??
            savedDeal?.dealFare ??
            savedDeal?.fullDeal?.dealFare,
          fareDetails:
            rowDealRootForTrace?.fareDetails ??
            savedDeal?.fareDetails ??
            savedDeal?.fullDeal?.fareDetails,
          rawFlightSources: searchCheapestFlight.rawFlightSources,
          cheapestFlightCandidate: searchCheapestFlight.candidate,
          cheapestFlightPrice: normalizedPricing?.flight?.total ?? null,
          currency: normalizedPricing?.flight?.currency ?? null,
          searchCardFlightSource: pricingTrace?.searchCardFlightSource ?? null,
          unavailableReason:
            normalizedPricing?.flight?.total == null
              ? pricingTrace?.unavailableReason ?? null
              : null,
        });
      } catch {
        /* ignore */
      }
    }

    breakdownRows =
      buildDynamicPackageSearchResultBreakdownRows(normalizedPricing);

    finalPrice = hotelApiPrice;
    const amountStr =
      finalPrice != null
        ? formatAgentHotelSearchApiPriceDisplay(finalPrice, reqCur)
        : null;
    priceLineParts =
      amountStr && typeof t === "function"
        ? {
            prefix: `${t("dynamicPackages.pricingHotelFromPrefix", "מחיר מלון (רשימה)")} `,
            amount: amountStr,
            perPerson: null,
          }
        : amountStr
          ? { prefix: "מחיר מלון (רשימה) ", amount: amountStr, perPerson: null }
          : null;

    currency =
      normalizedPricing?.currency ||
      (finalPrice != null && reqCur ? reqCur : finalPrice != null ? "API" : "—");
  }

  if (process.env.NODE_ENV === "development" && hotelsItem) {
    console.log("[dynamic-packages] view model built", {
      hotelId: id,
      hasOutbound: !!outbound,
      hasInbound: !!inbound,
      apiLowestPackagePrice: finalPrice,
    });
  }

  return {
    type: "dynamic_vacation_package",
    source: "gemonix+lds",
    id: id != null ? String(id) : null,
    packageId: id != null ? `dyn-${id}` : null,
    hotelId: id,
    hotelName: hotelName || "—",
    stars,
    locationLine,
    locationCity,
    locationCountry,
    heroImageUrls,
    heroImageUrl,
    nights,
    passengersCount,
    boardCode: boardCode || null,
    boardLabel,
    outbound,
    inbound,
    pricing: {
      hotelPrice: hotelApiPrice,
      flightsPrice: flightsPriceIls,
      basePrice: null,
      commission: null,
      extraFees: 0,
      finalPrice,
      currency,
      priceLineParts,
      normalized: normalizedPricing,
      breakdownRows,
      pricingTrace,
    },
    priceLineParts,
    dynamicPackagePriceBreakdown: breakdownRows,
    raw,
  };
}
